import React, { useEffect, useRef, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../../services/api";
import ProcedureStepEditor from "../ProcedureStepEditor";
import { parseCsv, toCsv, downloadCsv } from "../../../utils/csv";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";

interface ProcedureRow {
  id: number;
  code?: string;
  name: string;
  category?: string;
  cost?: number;
  default_fee?: number;
  requires_tooth?: boolean;
  master_id?: string;
  standard_code?: string;
  procedure_scope?: string;
  target_selector?: string;
  default_steps?: string[] | { label: string; chart_mutation: string }[];
  chart_mutation?: unknown;
  symbol_trigger_map?: unknown;
  is_active?: boolean;
}

interface FormState {
  code: string;
  name: string;
  category: string;
  cost: string;
  master_id: string;
  standard_code: string;
  default_steps: string;
  procedure_scope: string;
  target_selector: string;
  chart_mutation: string;
  symbol_trigger_map: string;
}

export default function Procedures() {
  const { showToast } = useToast();
  const [rows, setRows] = useState<ProcedureRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editingStepsId, setEditingStepsId] = useState<number | null>(null);
  const importRef = useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState<FormState>({
    code: "",
    name: "",
    category: "",
    cost: "",
    master_id: "",
    standard_code: "",
    default_steps: "",
    procedure_scope: "visit",
    target_selector: "none",
    chart_mutation: "",
    symbol_trigger_map: ""
  });
  const [editingId, setEditingId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet<ProcedureRow[]>("/api/admin/procedures");
      const list = Array.isArray(data) ? data : [];
      setRows(list.map((r) => ({ ...r, cost: r.default_fee ?? r.cost ?? 0 })));
    } catch (e) {
      showToast({ message: "Failed to load procedures", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this procedure? This may affect historical data.")) return;
    try {
      await apiDelete(`/api/admin/procedures/${id}`);
      showToast({ message: "Procedure deleted successfully.", type: "success" });
      load();
    } catch (e) {
      showToast({ message: "Failed to delete procedure", type: "error" });
    }
  };

  const handleExport = () => {
    if (!rows.length) {
      showToast({ message: "No data to export.", type: "warning" });
      return;
    }
    const headers = ["code", "name", "category", "cost", "requires_tooth", "is_active"];
    const data = rows.map((r) => ({
      code: r.code || "",
      name: r.name,
      category: r.category || "",
      cost: r.cost ?? r.default_fee ?? 0,
      requires_tooth: r.requires_tooth ?? true,
      is_active: r.is_active ?? true,
    }));
    const csv = toCsv(headers, data);
    downloadCsv(csv, "procedures_master.csv");
    showToast({ message: "Export downloaded.", type: "info" });
  };

  const handleImportFile = async (file: File) => {
    const text = await file.text();
    const rowsCsv = parseCsv(text);
    if (rowsCsv.length < 2) return showToast({ message: "CSV is empty", type: "error" });
    const headers = rowsCsv[0].map((h) => h.trim().toLowerCase());
    const dataRows = rowsCsv.slice(1);

    showToast({ message: "Importing CSV data...", type: "info" });
    
    let errorCount = 0;
    
    for (const row of dataRows) {
      const get = (key: string) => {
        const idx = headers.indexOf(key);
        return idx >= 0 ? (row[idx] ?? "").trim() : "";
      };
      const code = get("code");
      const name = get("name");
      if (!name) continue;
      const category = get("category");
      const costRaw = get("cost") || get("default_fee");
      const requiresToothRaw = get("requires_tooth");
      const requires_tooth = requiresToothRaw ? !["false", "0", "no"].includes(requiresToothRaw.toLowerCase()) : true;
      const payload = {
        code,
        name,
        category,
        cost: Number(costRaw || 0),
        requires_tooth,
      };

      try {
        const existing = rows.find((r) => (code && r.code === code) || r.name === name);
        if (existing) {
          await apiPut(`/api/admin/procedures/${existing.id}`, payload);
        } else {
          await apiPost("/api/admin/procedures", payload);
        }
      } catch (err) {
        errorCount++;
        console.error("Row import failed", row, err);
      }
    }
    
    if (errorCount > 0) {
      showToast({ message: `Import finished with ${errorCount} errors.`, type: "warning" });
    } else {
      showToast({ message: "Import successful.", type: "success" });
    }
    
    load();
  };

  const filtered = rows.filter(r => 
    r.name.toLowerCase().includes(search.toLowerCase()) || 
    (r.code || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="master-panel">
      {/* Toolbar */}
      <div className="master-toolbar">
        <input
          type="search"
          placeholder="Search procedures..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="master-search"
        />
        <div className="master-toolbar-actions">
          <input type="file" accept=".csv" ref={importRef} className="master-file-input" onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleImportFile(file);
            if (e.target) e.target.value = "";
          }} />
          <Button variant="outline" onClick={() => importRef.current?.click()}>Import CSV</Button>
          <Button variant="outline" onClick={handleExport}>Export CSV</Button>
          <Button variant="primary" onClick={() => {/* Trigger Add Form */}}>+ Add Procedure</Button>
        </div>
      </div>

      {/* Data Table */}
      <div className="master-table-wrap">
        <table className="master-table">
          <thead className="master-thead">
            <tr>
              <th className="master-th">Code</th>
              <th className="master-th">Procedure Name</th>
              <th className="master-th">Category</th>
              <th className="master-th">Base Fee (₹)</th>
              <th className="master-th">Status</th>
              <th className="master-th master-th-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
               <tr><td colSpan={6} className="master-empty">Loading catalog...</td></tr>
            ) : filtered.length === 0 ? (
               <tr><td colSpan={6} className="master-empty">No procedures found.</td></tr>
            ) : (
              filtered.map(r => (
                <tr key={r.id} className="master-row">
                  <td className="master-cell master-accent">{r.code || "—"}</td>
                  <td className="master-cell master-strong">{r.name}</td>
                  <td className="master-cell">
                    <span className="master-pill">{r.category || "—"}</span>
                  </td>
                  <td className="master-cell">₹{r.cost?.toLocaleString() || r.default_fee?.toLocaleString() || "0"}</td>
                  <td className="master-cell">
                    <span className={`master-status ${r.is_active ? "is-active" : "is-inactive"}`}>
                      {r.is_active ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td className="master-cell master-cell-right">
                    <div className="master-actions">
                        <Button variant="secondary" size="sm" onClick={() => setEditingStepsId(r.id)}>Steps</Button>
                        <Button variant="danger" size="sm" onClick={() => remove(r.id)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modern Blurred Editor Modal */}
      {editingStepsId && (
        <div className="master-modal-overlay" onClick={() => setEditingStepsId(null)}>
          <div className="master-modal-card" onClick={e => e.stopPropagation()}>
            <ProcedureStepEditor procedureId={editingStepsId} onClose={() => { setEditingStepsId(null); load(); }} />
          </div>
        </div>
      )}
    </div>
  );
}
